import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, Sparkles, Stethoscope, Clock, FileText, AlertTriangle, ChevronRight, Wrench, Lock, Mic, Square,
} from "lucide-react";
import api, { money } from "@/lib/api";
import localApi from "@/lib/localApi";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const LIKELY = { high: "text-rose-400 bg-rose-400/10", medium: "text-amber-400 bg-amber-400/10", low: "text-slate-400 bg-slate-400/10" };

export default function AiAssistant() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [entitled, setEntitled] = useState(null);

  useEffect(() => {
    localApi.get("/marketplace/subscription")
      .then(({ data }) => setEntitled(!!data.entitled))
      .catch(() => setEntitled(true)); // don't block if marketplace status unavailable
  }, []);

  // Diagnose
  const [dx, setDx] = useState({ codes: "", symptoms: "", vehicle: "", mileage: "" });
  const [dxLoading, setDxLoading] = useState(false);
  const [dxResult, setDxResult] = useState(null);

  // Voice dictation for symptoms
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const stopRecording = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return toast.error("Microphone not supported in this browser");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size === 0) return;
        setTranscribing(true);
        try {
          const b64 = await new Promise((res, rej) => {
            const r = new FileReader();
            r.onloadend = () => res(String(r.result).split(",")[1]);
            r.onerror = rej;
            r.readAsDataURL(blob);
          });
          const { data } = await api.post("/ai/transcribe", { audio_base64: b64, format: "webm" });
          const text = (data.text || data.transcript || "").trim();
          if (text) {
            setDx((d) => ({ ...d, symptoms: d.symptoms ? `${d.symptoms} ${text}` : text }));
            toast.success("Transcribed");
          } else {
            toast.message("No speech detected");
          }
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Transcription failed");
        } finally {
          setTranscribing(false);
        }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Microphone permission denied");
    }
  };

  const toggleRecording = () => (recording ? stopRecording() : startRecording());

  // Labor
  const [labor, setLabor] = useState({ repair: "", vehicle: "" });
  const [laborLoading, setLaborLoading] = useState(false);
  const [laborResult, setLaborResult] = useState(null);

  const runDiagnose = async () => {
    if (!dx.symptoms.trim() && !dx.codes.trim()) return toast.error("Enter symptoms or a trouble code");
    setDxLoading(true); setDxResult(null);
    try { const { data } = await api.post("/ai/diagnose", dx); setDxResult(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI request failed"); }
    finally { setDxLoading(false); }
  };

  const runLabor = async () => {
    if (!labor.repair.trim()) return toast.error("Describe the repair");
    setLaborLoading(true); setLaborResult(null);
    try { const { data } = await api.post("/ai/labor", labor); setLaborResult(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI request failed"); }
    finally { setLaborLoading(false); }
  };

  const laborToInvoice = () => {
    if (!laborResult) return;
    const rate = user?.labor_rate || 100;
    const items = [
      { description: laborResult.description || labor.repair, quantity: Number(laborResult.hours || 1), unit_price: rate, kind: "labor" },
      ...(laborResult.parts || []).map((p) => ({ description: p.name, quantity: 1, unit_price: Number(p.price || 0), kind: "part" })),
    ];
    navigate("/invoices/new", { state: { prefill: { items, vehicle: labor.vehicle } } });
  };

  const field = "h-10 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50";

  if (entitled === false) {
    return (
      <Layout title="AI Mechanic Assistant">
        <div className="max-w-lg mx-auto rounded-xl border border-amber-500/30 bg-amber-500/5 p-10 text-center animate-fade-up" data-testid="ai-paywall">
          <div className="h-12 w-12 rounded-xl bg-amber-500/20 flex items-center justify-center mx-auto mb-4"><Lock className="h-6 w-6 text-amber-400" /></div>
          <h3 className="font-head font-bold text-xl text-slate-50">AI tools are a Pro feature</h3>
          <p className="text-slate-400 mt-2">Your free trial has ended. Subscribe to Pitcrew Pro to use AI diagnostics and labor estimates.</p>
          <Button data-testid="ai-subscribe" onClick={() => navigate("/billing")} className="mt-6 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">Subscribe to Pitcrew Pro</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="AI Mechanic Assistant">
      <div className="max-w-4xl animate-fade-up">
        <div className="flex items-center gap-2 mb-5 text-slate-400 text-sm">
          <Sparkles className="h-4 w-4 text-amber-400" />
          Diagnose faults and estimate labor in seconds. Results are AI suggestions — always verify.
        </div>

        <Tabs defaultValue="diagnose">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="diagnose" data-testid="tab-diagnose" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950"><Stethoscope className="h-4 w-4 mr-1.5" /> Diagnose</TabsTrigger>
            <TabsTrigger value="labor" data-testid="tab-labor" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950"><Clock className="h-4 w-4 mr-1.5" /> Labor Estimate</TabsTrigger>
          </TabsList>

          {/* DIAGNOSE */}
          <TabsContent value="diagnose" className="mt-5 space-y-5">
            <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Vehicle</Label><Input data-testid="dx-vehicle" value={dx.vehicle} onChange={(e) => setDx({ ...dx, vehicle: e.target.value })} placeholder="2018 Toyota Camry" className={field} /></div>
                <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Mileage</Label><Input value={dx.mileage} onChange={(e) => setDx({ ...dx, mileage: e.target.value })} placeholder="95000" className={field} /></div>
                <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Trouble codes</Label><Input data-testid="dx-codes" value={dx.codes} onChange={(e) => setDx({ ...dx, codes: e.target.value })} placeholder="P0300, P0171" className={field} /></div>
                <div className="space-y-1.5 sm:col-span-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300 text-xs">Symptoms</Label>
                    <Button
                      type="button" size="sm" variant="ghost" data-testid="dx-mic-button"
                      onClick={toggleRecording} disabled={transcribing}
                      className={`h-7 px-2 text-xs ${recording ? "text-rose-400 hover:text-rose-300" : "text-amber-400 hover:text-amber-300"}`}
                    >
                      {transcribing ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Transcribing…</>
                        : recording ? <><Square className="h-3.5 w-3.5 mr-1 fill-current" /> Stop</>
                        : <><Mic className="h-3.5 w-3.5 mr-1" /> Dictate</>}
                    </Button>
                  </div>
                  <Textarea data-testid="dx-symptoms" value={dx.symptoms} onChange={(e) => setDx({ ...dx, symptoms: e.target.value })} placeholder="Rough idle, misfire, check engine light flashing…" className="bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
                  {recording && <p className="text-[11px] text-rose-400 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" /> Recording — tap Stop when done</p>}
                </div>
              </div>
              <Button data-testid="run-diagnose-button" onClick={runDiagnose} disabled={dxLoading} className="mt-4 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">{dxLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Diagnose</Button>
            </section>

            {dxLoading && <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin text-amber-500" /> Analyzing…</div>}

            {dxResult && (
              <div data-testid="dx-result" className="space-y-4">
                {dxResult.summary && (
                  <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] uppercase tracking-wide text-amber-400 font-semibold">Summary</p>
                      {dxResult.urgency && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {dxResult.urgency}</span>}
                    </div>
                    <p className="text-sm text-slate-200 leading-relaxed">{dxResult.summary}</p>
                  </div>
                )}
                {(dxResult.codes || []).length > 0 && (
                  <Section title="Trouble Codes">
                    {dxResult.codes.map((c, i) => (
                      <div key={c.code || i} className="py-2 border-b border-slate-800/60">
                        <div className="flex items-center gap-2"><span className="font-mono text-amber-400 text-sm">{c.code}</span>{c.severity && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">{c.severity}</span>}</div>
                        <p className="text-sm text-slate-400 mt-1">{c.meaning}</p>
                      </div>
                    ))}
                  </Section>
                )}
                {(dxResult.likely_causes || []).length > 0 && (
                  <Section title="Likely Causes">
                    {dxResult.likely_causes.map((c, i) => (
                      <div key={c.cause || i} className="py-2.5 border-b border-slate-800/60">
                        <div className="flex items-center gap-2"><span className="text-sm font-medium text-slate-100">{c.cause}</span><span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${LIKELY[c.likelihood] || LIKELY.low}`}>{c.likelihood}</span></div>
                        {c.why && <p className="text-xs text-slate-500 mt-1">{c.why}</p>}
                      </div>
                    ))}
                  </Section>
                )}
                {(dxResult.diagnostic_steps || []).length > 0 && (
                  <Section title="Diagnostic Steps">
                    <ol className="space-y-1.5">{dxResult.diagnostic_steps.map((s, i) => (<li key={`step-${i}`} className="text-sm text-slate-300 flex gap-2"><ChevronRight className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" /><span>{s}</span></li>))}</ol>
                  </Section>
                )}
                {(dxResult.recommended_repairs || []).length > 0 && (
                  <Section title="Recommended Repairs">
                    {dxResult.recommended_repairs.map((r, i) => (
                      <div key={r.repair || i} className="py-2 border-b border-slate-800/60">
                        <p className="text-sm font-medium text-slate-100 flex items-center gap-2"><Wrench className="h-3.5 w-3.5 text-slate-500" /> {r.repair}</p>
                        {r.notes && <p className="text-xs text-slate-500 mt-1 pl-5">{r.notes}</p>}
                      </div>
                    ))}
                  </Section>
                )}
              </div>
            )}
          </TabsContent>

          {/* LABOR */}
          <TabsContent value="labor" className="mt-5 space-y-5">
            <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 sm:col-span-2"><Label className="text-slate-300 text-xs">Repair / service</Label><Textarea data-testid="labor-repair" value={labor.repair} onChange={(e) => setLabor({ ...labor, repair: e.target.value })} placeholder="Replace front brake pads and rotors" className="bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" /></div>
                <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Vehicle</Label><Input data-testid="labor-vehicle" value={labor.vehicle} onChange={(e) => setLabor({ ...labor, vehicle: e.target.value })} placeholder="2020 Honda Civic" className={field} /></div>
              </div>
              <Button data-testid="run-labor-button" onClick={runLabor} disabled={laborLoading} className="mt-4 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">{laborLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Estimate</Button>
            </section>

            {laborLoading && <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin text-amber-500" /> Estimating…</div>}

            {laborResult && (
              <div data-testid="labor-result" className="rounded-xl border border-slate-800 bg-slate-900 p-5 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-slate-100">{laborResult.description}</p>
                    <p className="text-sm text-slate-400 flex items-center gap-1.5 mt-1"><Clock className="h-3.5 w-3.5" /> {laborResult.hours} labor hours · {money((laborResult.hours || 0) * (user?.labor_rate || 100))} @ {money(user?.labor_rate || 100)}/hr</p>
                  </div>
                  <Button data-testid="labor-to-invoice" onClick={laborToInvoice} className="bg-blue-600 hover:bg-blue-500 text-white"><FileText className="h-4 w-4 mr-1.5" /> Create Invoice</Button>
                </div>
                {(laborResult.parts || []).length > 0 && (
                  <div className="pt-3 border-t border-slate-800">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">Suggested Parts</p>
                    <div className="space-y-1.5">
                      {laborResult.parts.map((p, i) => (
                        <div key={p.name || i} className="flex items-center gap-2 text-sm">
                          <span className="text-slate-300 flex-1">{p.name}</span>
                          <span className="text-slate-200 tabular-nums font-medium">{money(p.price)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
      <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2">{title}</p>
      {children}
    </div>
  );
}
